import ChevronLeftIcon from '@ordr-as/icons/ChevronLeftIcon';
import { Dialog } from '@ordr-as/umami-react/dialog';
import type { FC } from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import type { CartOrderLine, CartOrderLineComponent } from '../../../../../../../types/cart';
import { useScreenSize } from '../../../../../../context/ScreenSizeContext';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import { useMenuCourses } from '../../../../../../context/MenuSectionsStore';
import { getUniqueOrderLineKey } from '../../../../../../utils/orderLineUtils';
import ComponentListRow from '../Edit/composite/ComponentListRow';
import type { ExpandedComponent } from '../Edit/composite/types';
import { collapseComponents, expandComponents, isCompositeComponent, toBaseComponent } from '../Edit/composite/types';
import { useCompositeNavigation } from '../Edit/composite/useCompositeNavigation';
import CoursePicker from './CoursePicker';
import SelectableComponentRow from './SelectableComponentRow';
import SelectableParentRow from './SelectableParentRow';

interface Props {
  onClose: VoidFunction;
  parentOrderLine: CartOrderLine;
}

type Phase = 'selection' | 'course-picker';

/** Recursively collect all expanded component instanceIds with their current courseIds */
const collectComponentCourseIds = (components: ExpandedComponent[]): Map<string, string | undefined> => {
  const result = new Map<string, string | undefined>();
  for (const component of components) {
    result.set(component.instanceId, component.courseId);
    if (component.components && component.components.length > 0) {
      for (const [id, courseId] of collectComponentCourseIds(component.components)) {
        result.set(id, courseId);
      }
    }
  }
  return result;
};

/** Collect all leaf (non-composite) component instanceIds */
const collectLeafIds = (components: ExpandedComponent[]): string[] => {
  const ids: string[] = [];
  for (const component of components) {
    if (isCompositeComponent(component)) {
      ids.push(...collectLeafIds(component.components!));
    }
    if (!isCompositeComponent(component)) {
      ids.push(component.instanceId);
    }
  }
  return ids;
};

const CompositeChangeCourseDialog: FC<Props> = ({ parentOrderLine, onClose }) => {
  const { isPhone } = useScreenSize();
  const courses = useMenuCourses();
  const updateCompositeProduct = useCartStore((x) => x.updateCompositeProduct);
  const splitCompositeProduct = useCartStore((x) => x.splitCompositeProduct);

  const expandedRootComponents = useMemo(
    () => expandComponents(parentOrderLine.components ?? []),
    [parentOrderLine.components]
  );
  const isFlatComposite = useMemo(
    () => expandedRootComponents.every((c) => !isCompositeComponent(c)),
    [expandedRootComponents]
  );

  const { push, pop, reset, currentTitle, viewMode, listComponents, canGoBack } =
    useCompositeNavigation(expandedRootComponents);

  const [phase, setPhase] = useState<Phase>('selection');
  const [componentCourseMap] = useState<Map<string, string | undefined>>(() => {
    if (isFlatComposite) {
      const map = new Map<string, string | undefined>();
      for (let i = 0; i < parentOrderLine.quantity; i++) {
        for (const component of expandedRootComponents) {
          map.set(`${i}:${component.instanceId}`, component.courseId);
        }
      }
      return map;
    }
    return collectComponentCourseIds(expandedRootComponents);
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const allLeafIds = useMemo(() => collectLeafIds(expandedRootComponents), [expandedRootComponents]);

  const getCourseName = useCallback(
    (courseId: string | undefined) => {
      if (!courseId) {
        return 'No course';
      }
      return courses.find((c) => c.courseId === courseId)?.name ?? 'No course';
    },
    [courses]
  );

  const toggleId = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
        return next;
      }
      next.delete(id);
      return next;
    });
  }, []);

  const toggleInstanceLeaves = useCallback(
    (instanceIndex: number, selected: boolean) => {
      const instanceIds = allLeafIds.map((id) => `${instanceIndex}:${id}`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of instanceIds) {
          if (selected) {
            next.add(id);
          }
          if (!selected) {
            next.delete(id);
          }
        }
        return next;
      });
    },
    [allLeafIds]
  );

  const saveWithMap = useCallback(
    (courseMap: Map<string, string | undefined>) => {
      if (!isFlatComposite) {
        // For nested composites, build full component tree with updated courses
        updateCompositeProduct({
          componentUpdates: [],
          components: collapseComponents(buildSaveComponentsFromCourseMap(expandedRootComponents, courseMap)),
          orderLineKey: getUniqueOrderLineKey(parentOrderLine),
          parentComment: parentOrderLine.comment,
          parentCourseId: parentOrderLine.courseId,
          parentOptions: parentOrderLine.options,
          quantity: parentOrderLine.quantity,
        });
        return;
      }

      // Parse instance-prefixed course map into per-instance maps
      const instanceMaps = new Map<number, Map<string, string | undefined>>();
      for (const [key, courseId] of courseMap) {
        const colonIdx = key.indexOf(':');
        if (colonIdx === -1) {
          continue;
        }
        const instanceIdx = Number.parseInt(key.slice(0, colonIdx), 10);
        const realId = key.slice(colonIdx + 1);
        if (!instanceMaps.has(instanceIdx)) {
          instanceMaps.set(instanceIdx, new Map());
        }
        instanceMaps.get(instanceIdx)!.set(realId, courseId);
      }

      // Group instances with identical course configs
      const groupMap = new Map<string, { quantity: number; courseMap: Map<string, string | undefined> }>();
      for (const [, instMap] of instanceMaps) {
        const groupKey = JSON.stringify([...instMap.entries()].sort(([a], [b]) => a.localeCompare(b)));
        const existing = groupMap.get(groupKey);
        if (existing) {
          existing.quantity += 1;
        }
        if (!existing) {
          groupMap.set(groupKey, { courseMap: instMap, quantity: 1 });
        }
      }

      const groups = [...groupMap.values()];

      // If all instances have the same config, use regular update (no split)
      if (groups.length <= 1) {
        const singleMap = groups[0]?.courseMap ?? new Map();
        updateCompositeProduct({
          componentUpdates: [],
          components: collapseComponents(buildSaveComponentsFromCourseMap(expandedRootComponents, singleMap)),
          orderLineKey: getUniqueOrderLineKey(parentOrderLine),
          parentComment: parentOrderLine.comment,
          parentCourseId: parentOrderLine.courseId,
          parentOptions: parentOrderLine.options,
          quantity: parentOrderLine.quantity,
        });
        return;
      }

      splitCompositeProduct({
        groups: groups.map((g) => ({
          quantity: g.quantity,
          componentUpdates: buildComponentUpdatesFromCourseMap(expandedRootComponents, g.courseMap),
        })),
        orderLineKey: getUniqueOrderLineKey(parentOrderLine),
        parentComment: parentOrderLine.comment,
        parentCourseId: parentOrderLine.courseId,
        parentOptions: parentOrderLine.options,
      });
    },
    [isFlatComposite, updateCompositeProduct, splitCompositeProduct, parentOrderLine, expandedRootComponents]
  );

  const applyCourseToSelection = useCallback(
    (courseId: string | undefined) => {
      const updatedMap = new Map(componentCourseMap);
      for (const id of selectedIds) {
        updatedMap.set(id, courseId);
      }
      saveWithMap(updatedMap);
      onClose();
    },
    [selectedIds, componentCourseMap, saveWithMap, onClose]
  );

  const handleClose = useCallback(() => {
    saveWithMap(componentCourseMap);
    onClose();
  }, [saveWithMap, componentCourseMap, onClose]);

  const handleBackFromPicker = useCallback(() => {
    setSelectedIds(new Set());
    reset();
    setPhase('selection');
  }, [reset]);

  // Compute common courseId for pre-selecting in the picker
  const commonCourseId = useMemo(() => {
    if (selectedIds.size === 0) {
      return undefined;
    }
    const ids = [...selectedIds];
    const first = componentCourseMap.get(ids[0]!);
    const allSame = ids.every((id) => componentCourseMap.get(id) === first);
    if (!allSame) {
      return undefined;
    }
    return first;
  }, [selectedIds, componentCourseMap]);

  // Dialog chrome
  const isSelectionPhase = phase === 'selection';
  const hasSelection = selectedIds.size > 0;
  const showBackButton = isSelectionPhase ? canGoBack : true;

  const title = (() => {
    if (!isSelectionPhase) {
      return `${selectedIds.size} products`;
    }
    if (currentTitle) {
      return currentTitle;
    }
    return 'Select products';
  })();

  const handleBack = (() => {
    if (!isSelectionPhase) {
      return handleBackFromPicker;
    }
    return pop;
  })();

  return (
    <Dialog
      onClose={handleClose}
      title={title}
      actionLabel={isSelectionPhase ? 'Select course' : undefined}
      onAction={isSelectionPhase ? () => setPhase('course-picker') : undefined}
      isDisabledAction={!hasSelection}
      size={isPhone ? 'fullwidth' : 'default'}
      TitleIcon={showBackButton ? ChevronLeftIcon : undefined}
      titleIconAction={showBackButton ? handleBack : undefined}
      titleIconClassNames={showBackButton ? '!mr-1 !p-0' : undefined}
      alignTop
    >
      <article className="flex flex-col gap-2">
        {phase === 'course-picker' && (
          <CoursePicker courses={courses} selectedCourseId={commonCourseId} onChange={applyCourseToSelection} />
        )}

        {isSelectionPhase && isFlatComposite && viewMode === 'root' && (
          <FlatSelectionView
            parentName={parentOrderLine.name}
            rootComponents={expandedRootComponents}
            selectedIds={selectedIds}
            componentCourseMap={componentCourseMap}
            getCourseName={getCourseName}
            onToggleInstance={toggleInstanceLeaves}
            onToggle={toggleId}
            quantity={parentOrderLine.quantity}
          />
        )}

        {isSelectionPhase && !isFlatComposite && (viewMode === 'root' || viewMode === 'component-list') && (
          <NestedSelectionView
            listComponents={listComponents}
            selectedIds={selectedIds}
            componentCourseMap={componentCourseMap}
            getCourseName={getCourseName}
            onToggle={toggleId}
            onNavigate={push}
          />
        )}
      </article>
    </Dialog>
  );
};

// --- Helpers for building save data from expanded components ---

/** Build full component tree from expanded state with course map applied */
const buildSaveComponentsFromCourseMap = (
  expanded: ExpandedComponent[],
  courseMap: Map<string, string | undefined>
): CartOrderLineComponent[] =>
  expanded.map((ec) => {
    const courseId = courseMap.has(ec.instanceId) ? courseMap.get(ec.instanceId) : ec.courseId;
    const children = ec.components as ExpandedComponent[] | undefined;
    return toBaseComponent(ec, {
      components: children ? buildSaveComponentsFromCourseMap(children, courseMap) : undefined,
      courseId,
    });
  });

/** Build componentUpdates array for split (legacy format needed by splitCompositeProduct) */
const buildComponentUpdatesFromCourseMap = (
  expanded: ExpandedComponent[],
  courseMap: Map<string, string | undefined>
): {
  itemVariantId: string;
  options: CartOrderLineComponent['options'];
  comment?: string;
  courseId?: string;
}[] => {
  const result: {
    itemVariantId: string;
    options: CartOrderLineComponent['options'];
    comment?: string;
    courseId?: string;
  }[] = [];

  const walk = (components: ExpandedComponent[]) => {
    for (const component of components) {
      const courseId = courseMap.has(component.instanceId) ? courseMap.get(component.instanceId) : component.courseId;
      result.push({
        comment: component.comment,
        courseId,
        itemVariantId: component.itemVariantId,
        options: component.options,
      });
      if (component.components) {
        walk(component.components);
      }
    }
  };
  walk(expanded);
  return result;
};

// --- Sub-components for rendering selection views ---

interface FlatSelectionViewProps {
  parentName: string;
  rootComponents: ExpandedComponent[];
  selectedIds: Set<string>;
  componentCourseMap: Map<string, string | undefined>;
  getCourseName: (courseId: string | undefined) => string;
  onToggleInstance: (instanceIndex: number, selected: boolean) => void;
  onToggle: (id: string, selected: boolean) => void;
  quantity: number;
}

const FlatSelectionView: FC<FlatSelectionViewProps> = ({
  parentName,
  rootComponents,
  selectedIds,
  componentCourseMap,
  getCourseName,
  onToggleInstance,
  onToggle,
  quantity,
}) => (
  <>
    {Array.from({ length: quantity }, (_, i) => {
      const instanceIds = rootComponents.map((c) => `${i}:${c.instanceId}`);
      const allSelected = instanceIds.every((id) => selectedIds.has(id));
      return (
        <Fragment key={i}>
          <SelectableParentRow
            parentName={parentName}
            childDetails={[]}
            isSelected={allSelected}
            onToggle={(selected) => onToggleInstance(i, selected)}
          />
          {rootComponents.map((component) => {
            const prefixedId = `${i}:${component.instanceId}`;
            return (
              <SelectableComponentRow
                key={prefixedId}
                component={component}
                courseName={getCourseName(componentCourseMap.get(prefixedId))}
                isSelected={selectedIds.has(prefixedId)}
                onToggle={(selected) => onToggle(prefixedId, selected)}
              />
            );
          })}
        </Fragment>
      );
    })}
  </>
);

interface NestedSelectionViewProps {
  listComponents: ExpandedComponent[];
  selectedIds: Set<string>;
  componentCourseMap: Map<string, string | undefined>;
  getCourseName: (courseId: string | undefined) => string;
  onToggle: (id: string, selected: boolean) => void;
  onNavigate: (segment: { instanceId: string; itemVariantId: string; name: string }) => void;
}

const NestedSelectionView: FC<NestedSelectionViewProps> = ({
  listComponents,
  selectedIds,
  componentCourseMap,
  getCourseName,
  onToggle,
  onNavigate,
}) => (
  <>
    {listComponents.map((component) => {
      if (isCompositeComponent(component)) {
        return (
          <ComponentListRow
            key={component.instanceId}
            component={component}
            subtitle={getCourseName(componentCourseMap.get(component.instanceId))}
            isValid
            onClick={() =>
              onNavigate({
                instanceId: component.instanceId,
                itemVariantId: component.itemVariantId,
                name: component.name,
              })
            }
          />
        );
      }
      return (
        <SelectableComponentRow
          key={component.instanceId}
          component={component}
          courseName={getCourseName(componentCourseMap.get(component.instanceId))}
          isSelected={selectedIds.has(component.instanceId)}
          onToggle={(selected) => onToggle(component.instanceId, selected)}
        />
      );
    })}
  </>
);

export default CompositeChangeCourseDialog;
